// Dashboard / Home screen — the most important screen (build brief priority #1: the
// Quick Add form sits above the budget summary per the latest revision).

import { icon, iconBadge } from '../lib/icons';
import { fmt, monthShort, todayLocal } from '../lib/format';
import { baseSpentApprox, nonBaseTotalsRaw, pctUsed, ringColor } from '../lib/calc';
import { state } from '../state';
import { getPhotoBlob } from '../lib/db';
import { blobToObjectURL } from '../lib/image';
import { renderAddButton, renderNav, renderOfflineBanner, expenseRow, emptyState, escapeHtml } from './shared';

const RING_CIRCUMFERENCE = 238.76; // 2*PI*38, matches the SVG r=38 ring

export function renderDashboard(): string {
  const spent = baseSpentApprox(state.expenses);
  const pct = pctUsed(state.expenses, state.trip.totalBudget);
  const remaining = state.trip.totalBudget - spent;
  const chips = Object.entries(nonBaseTotalsRaw(state.expenses, state.trip.baseCurrency))
    .map(([c, v]) => `<span class="chip">${fmt(v, c)}</span>`)
    .join('');
  const recent = [...state.expenses].sort((a, b) => b.date.localeCompare(a.date)).slice(0, 5);
  const t = state.trip;

  return `
  <div class="screen">
    ${renderOfflineBanner()}
    <div class="hero" id="heroBanner">
      <div class="hero-top-row">
        <div class="hero-dates">${t.startDate ? monthShort(t.startDate) : ''}${t.startDate && t.endDate ? ' – ' : ''}${
    t.endDate ? monthShort(t.endDate) : ''
  }</div>
        ${renderAddButton()}
      </div>
      <div class="hero-peaks"></div>
      <div class="hero-content">
        <div class="hero-eyebrow"><span class="dot"></span> ${t.countries.length}-country road trip</div>
        <h1>${escapeHtml(t.name || 'Your trip')}</h1>
        <div class="route-strip">${t.countries
          .map((c, i) => `<span>${escapeHtml(c)}</span>${i < t.countries.length - 1 ? '<span class="seg">→</span>' : ''}`)
          .join('')}</div>
        <div class="hero-desc">${escapeHtml(t.description)}</div>
      </div>
    </div>

    <div class="perf"></div>

    <div class="section-label" style="padding-top:6px;">Quick add expense</div>
    <div class="card" style="margin-top:6px;">
      <form onsubmit="return App.submitQuickExpense(event)">
        <div class="field amount">
          <label for="qeAmount">Amount</label>
          <input type="number" inputmode="decimal" step="0.01" min="0.01" id="qeAmount" placeholder="0.00" required autocomplete="off"/>
        </div>
        <div class="row2">
          <div class="field">
            <label for="qeDate">Date</label>
            <input type="date" id="qeDate" value="${todayLocal()}" required/>
          </div>
          <div class="field">
            <label for="qeCurrency">Currency</label>
            <select id="qeCurrency">${state.currencies
              .map((c) => `<option value="${c.code}" ${c.code === state.lastCurrency ? 'selected' : ''}>${c.code}</option>`)
              .join('')}</select>
          </div>
        </div>
        <div class="field">
          <label for="qeCategory">Category</label>
          <select id="qeCategory" ${state.categories.length ? '' : 'disabled'}>${
    state.categories.length
      ? state.categories.map((c) => `<option value="${c.id}">${escapeHtml(c.name)}</option>`).join('')
      : '<option>Add a category in Settings first</option>'
  }</select>
        </div>
        <div class="field">
          <label for="qeNote">Explanation (optional)</label>
          <input type="text" id="qeNote" placeholder="What was this expense for?" autocomplete="off"/>
        </div>
        <button type="submit" class="btn btn-primary btn-block" ${state.categories.length ? '' : 'disabled'}>＋ Add expense</button>
      </form>
    </div>

    <div class="card">
      <div class="card-title"><h2>Trip budget</h2><button class="link" onclick="App.setTab('reports')">Reports →</button></div>
      <div class="budget-row">
        <div class="ring">
          <svg width="88" height="88" viewBox="0 0 88 88" role="img" aria-label="${pct}% of budget used">
            <circle cx="44" cy="44" r="38" fill="none" stroke="var(--card-2)" stroke-width="8"/>
            <circle id="ringHome" cx="44" cy="44" r="38" fill="none" stroke="${ringColor(pct)}" stroke-width="8" stroke-linecap="round" stroke-dasharray="${RING_CIRCUMFERENCE}" stroke-dashoffset="${RING_CIRCUMFERENCE}"/>
          </svg>
          <div class="ring-pct">${pct}%</div>
        </div>
        <div class="budget-figs">
          <div class="budget-line"><span>Spent (≈)</span><b>${fmt(spent, t.baseCurrency)}</b></div>
          <div class="budget-line"><span>Budget</span><b>${fmt(t.totalBudget, t.baseCurrency)}</b></div>
          <div class="budget-line"><span>Remaining</span><b style="color:${remaining < 0 ? 'var(--red)' : 'var(--text)'}">${fmt(
    remaining,
    t.baseCurrency
  )}</b></div>
        </div>
      </div>
      ${
        chips
          ? `<div class="chip-row">${chips}</div><div class="budget-note">≈ approximate conversion to ${t.baseCurrency} for display only, using fixed reference rates. Chips above show unconverted totals actually spent in each currency — currencies are never silently combined.</div>`
          : ''
      }
    </div>

    <div class="action-row">
      <button class="action-tile camera" onclick="document.getElementById('cameraInput').click()">
        ${iconBadge('camera', { size: 'icon-badge-lg', iconSize: 22 })}<div class="lbl">Take photo</div>
      </button>
      <button class="action-tile" onclick="App.setTab('map')">
        ${iconBadge('marker', { size: 'icon-badge-lg', iconSize: 22 })}<div class="lbl">Open photo map</div>
      </button>
    </div>
    <input type="file" accept="image/*" capture="environment" id="cameraInput" class="visually-hidden" onchange="App.handlePhotoCapture(event)"/>

    <div class="section-label">Recent expenses<button class="link" onclick="App.setTab('expenses')">See all →</button></div>
    <div>
      ${recent.length ? recent.map(expenseRow).join('') : emptyState('inbox', 'No expenses yet', 'Add your first trip expense above.')}
    </div>
  </div>
  ${renderNav()}`;
}

export function mountDashboard(): void {
  const el = document.getElementById('ringHome');
  const pct = pctUsed(state.expenses, state.trip.totalBudget);
  if (el) {
    const offset = RING_CIRCUMFERENCE - (RING_CIRCUMFERENCE * pct) / 100;
    requestAnimationFrame(() => {
      (el as unknown as SVGCircleElement).style.transition = 'stroke-dashoffset 900ms cubic-bezier(.16,1,.3,1)';
      (el as unknown as SVGCircleElement).style.strokeDashoffset = String(offset);
    });
  }
  if (state.trip.bannerImageId) {
    getPhotoBlob(state.trip.bannerImageId, 'original').then((blob) => {
      const hero = document.getElementById('heroBanner');
      if (blob && hero) {
        hero.classList.add('has-image');
        hero.style.backgroundImage = `url(${blobToObjectURL(blob)})`;
      }
    });
  }
}
